import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Bell, Check, Shield, CreditCard, Headphones, Activity, 
  Star, Users, Lock, LogIn, ShieldCheck, ShieldOff, LogOut,
  CheckCircle, XCircle, RefreshCw, Clock, FileText, 
  MessageSquare, Archive, Layout, Trash2, UserPlus, Calendar, Key,
  ArrowUp, ArrowDown, AlertTriangle, Mail, UserMinus, Volume2, VolumeX
} from "lucide-react";
import { toast } from "sonner";
import { getApiUrl } from '../config/appConfig';

// Use auto-detected getAPI() URL
const getAPI = () => getApiUrl();

// Notification sound URL (using a simple notification sound)
const NOTIFICATION_SOUND_URL = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleRADY5z/s5lgFANMl/20m2cbBTqK+bydZRgDMoT+vZ9jFQUoh/+6m2AUBRmC/7ibXREFD37/tZZaEAQFev+ylFcOAwN4/7CSVAwDAnb/r5FUCwIBdf+ukVMKAgF1/66RUwoCAnb/sJNWDAMDeP+zlFkPBAZ8/7WXXBEFCn//uJphFAYNhP+7nmUXBxKJ/76hahsFGI7/wa5wHgkdkf/DsHQhCiGT/8SydiMKJJX/xbR4JQsnl//GuXomCimZ/8e6eyYLK5r/yLt8Jwstm//IvH0oCy+c/8m9fygLL5z/yb1/KAsvnP/JvX8oCy+c/8m9fygLL5z/yb1/KAsvm//IvH4oCyua/8i7fCcLKJn/x7p6Jgsmmf/Gunommin/x7l5JQonl//GtngnCySV/8W0diMLIJT/xLJ0IQkckf/DsHEfCRmO/8GubiAIFYv/wKprHQgRh/+9pWcbBw6E/7ugYxgGCoD/uJxfFQUGfP+2mFsRBAJ4/7OVVw4DAXb/sZJUDAIBdP+wkVMKAgFz/6+RUgoCAnT/sJFUCwIBdP+xklUMAQN1/7KTVg0DAnd/tJVYDgMEef+1l1oPBAZ8/7eaXREFFn//up5lFQYKgf+8oWgXBg6F/7+kax0HEon/wadwIAgWjP/Cq3QiCSCR/8WveScKJJT/x7N7KAonl//Itn4pCyqa/8q5gCoML5z/y7uBKwwwnv/MvYMsDTGf/829gy0MM6D/zb6ELAA="

// Icon mapping
const ICON_MAP = {
  Lock, LogIn, ShieldCheck, ShieldOff, LogOut,
  CheckCircle, XCircle, RefreshCw, Clock, FileText,
  MessageSquare, Archive, Layout, Trash2, UserPlus, Calendar, Key,
  ArrowUp, ArrowDown, AlertTriangle, Mail, UserMinus,
  Shield, CreditCard, Headphones, Activity, Star, Users, Bell
};

// Category icons and colors
const CATEGORY_CONFIG = {
  security: { icon: Shield, color: "text-red-600", bg: "bg-red-100" },
  payments: { icon: CreditCard, color: "text-green-600", bg: "bg-green-100" },
  support: { icon: Headphones, color: "text-violet-600", bg: "bg-violet-100" },
  activity: { icon: Activity, color: "text-blue-600", bg: "bg-blue-100" },
  subscription: { icon: Star, color: "text-amber-600", bg: "bg-amber-100" },
  team: { icon: Users, color: "text-indigo-600", bg: "bg-indigo-100" }
};

export default function NotificationBell({ isAdmin = false, context = "user", onOpenTicket, onGoToSupport }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [categoryCount, setCategoryCount] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [soundMuted, setSoundMuted] = useState(false);
  const [lastNotificationId, setLastNotificationId] = useState(null);
  const dropdownRef = useRef(null);
  const audioRef = useRef(null);

  // Context for filtering - "user" for user dashboard, "admin" for backoffice
  const notificationContext = isAdmin ? "admin" : context;

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
    audioRef.current.volume = 0.5;
    
    // Load sound preference from localStorage
    const savedMuted = localStorage.getItem('notification_sound_muted');
    if (savedMuted !== null) {
      setSoundMuted(savedMuted === 'true');
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!soundMuted && audioRef.current) {
      audioRef.current.play().catch(e => console.log("Audio play failed:", e));
    }
  }, [soundMuted]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      // Get unread count filtered by context
      const countRes = await axios.get(`${getAPI()}/notifications/count?context=${notificationContext}`, { withCredentials: true });
      const newCount = countRes.data.unread_count || 0;
      const newCategoryCount = countRes.data.by_category || {};
      
      // Check if we have new notifications (for sound)
      if (newCount > unreadCount && lastNotificationId) {
        playNotificationSound();
      }
      
      setUnreadCount(newCount);
      setCategoryCount(newCategoryCount);

      // Get recent notifications
      const notifsRes = await axios.get(`${getAPI()}/notifications/?limit=10&unread_only=false&context=${notificationContext}`, { withCredentials: true });
      const notifs = notifsRes.data.notifications || [];
      setNotifications(notifs);
      
      // Track last notification ID
      if (notifs.length > 0) {
        setLastNotificationId(notifs[0].notification_id);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      // Fallback to support tickets for backward compatibility
      try {
        const fallbackRes = await axios.get(`${getAPI()}/support/notifications/count`, { withCredentials: true });
        setUnreadCount(fallbackRes.data.unread_count || 0);
      } catch (e) {
        console.error("Fallback also failed:", e);
      }
    }
  }, [notificationContext, unreadCount, lastNotificationId, playNotificationSound]);

  // Fetch notifications on mount and every 5 minutes
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, []);
// Mark all as read
  const markAllRead = async () => {
    setLoading(true);
    try {
      await axios.post(`${getAPI()}/notifications/mark-read`, { mark_all: true }, { withCredentials: true });
      setUnreadCount(0);
      setCategoryCount({});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      // Fallback to old endpoint
      try {
        await axios.post(`${getAPI()}/support/notifications/mark-read`, {}, { withCredentials: true });
        setUnreadCount(0);
        toast.success("Notifications marked as read");
      } catch (e) {
        toast.error("Failed to mark notifications as read");
      }
    } finally {
      setLoading(false);
    }
  };

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${getAPI()}/notifications/mark-read`, { notification_ids: [notificationId] }, { withCredentials: true });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  // Toggle sound mute
  const toggleSound = () => {
    const newMuted = !soundMuted;
    setSoundMuted(newMuted);
    localStorage.setItem('notification_sound_muted', String(newMuted));
    toast.success(newMuted ? "Notification sound muted" : "Notification sound enabled");
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      await markAsRead(notification.notification_id);
    }
    
    setIsOpen(false);
    
    // Navigate based on notification type
    if (notification.link_route) {
      navigate(notification.link_route);
    } else if (notification.link_to) {
      // Build route from link_to and link_id
      switch (notification.link_to) {
        case "support":
        case "support_ticket":
          if (onOpenTicket && notification.link_id) {
            onOpenTicket(notification.link_id);
          } else if (onGoToSupport) {
            onGoToSupport();
          } else {
            navigate(`/dashboard?tab=support${notification.link_id ? `&ticket=${notification.link_id}` : ''}`);
          }
          break;
        case "settings":
          navigate("/dashboard?tab=settings");
          break;
        case "billing":
          navigate("/dashboard?tab=billing");
          break;
        case "team":
          navigate("/dashboard?tab=team");
          break;
        case "layouts":
          navigate("/dashboard?tab=layouts");
          break;
        case "leads":
          navigate("/backoffice/leads");
          break;
        case "layout":
          if (notification.link_id) {
            navigate(`/builder/${notification.link_id}`);
          } else {
            navigate("/dashboard?tab=layouts");
          }
          break;
        default:
          navigate("/dashboard");
      }
    } else if (notification.category === "support" && notification.data?.ticket_id) {
      // Legacy support ticket notification
      if (onOpenTicket) {
        onOpenTicket(notification.data.ticket_id);
      } else {
        navigate(`/dashboard?tab=support&ticket=${notification.data.ticket_id}`);
      }
    }
  };

  // Handle view all
  const handleViewAll = () => {
    setIsOpen(false);
    navigate("/dashboard?tab=settings&section=notifications");
  };

  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get icon component
  const getIconComponent = (iconName, category) => {
    const IconComponent = ICON_MAP[iconName] || CATEGORY_CONFIG[category]?.icon || Bell;
    return IconComponent;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) fetchNotifications(); // Refresh when opening
        }}
        className={`relative p-2 rounded-full transition-colors ${
          unreadCount > 0 ? 'hover:bg-violet-50' : 'hover:bg-gray-100'
        }`}
        title={unreadCount > 0 ? `${unreadCount} notification${unreadCount !== 1 ? 's' : ''}` : 'No notifications'}
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-violet-600' : 'text-gray-400'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Mobile overlay */}
          <div 
            className="fixed inset-0 bg-black/20 z-[99] sm:hidden"
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 top-16 sm:top-auto sm:mt-2 w-auto sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-[100] max-h-[calc(100vh-5rem)] sm:max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="px-3 sm:px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                Notifications {unreadCount > 0 && <span className="text-violet-600">({unreadCount})</span>}
              </h3>
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Sound Toggle */}
              <button
                onClick={toggleSound}
                className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                title={soundMuted ? "Unmute notifications" : "Mute notifications"}
              >
                {soundMuted ? (
                  <VolumeX className="w-4 h-4 text-gray-400" />
                ) : (
                  <Volume2 className="w-4 h-4 text-violet-600" />
                )}
              </button>
              
              {/* Mark All Read */}
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  disabled={loading}
                  className="text-xs text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1 px-2 py-1 rounded hover:bg-violet-50"
                >
                  <Check className="w-3 h-3" />
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Category Chips */}
          {Object.keys(categoryCount).length > 0 && (
            <div className="px-3 sm:px-4 py-2 border-b border-gray-100 flex gap-2 overflow-x-auto flex-shrink-0 scrollbar-hide">
              {Object.entries(categoryCount).map(([category, count]) => {
                const config = CATEGORY_CONFIG[category];
                if (!config || count === 0) return null;
                const CategoryIcon = config.icon;
                return (
                  <div 
                    key={category}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full ${config.bg} ${config.color} text-[10px] sm:text-xs font-medium whitespace-nowrap`}
                  >
                    <CategoryIcon className="w-3 h-3" />
                    <span className="capitalize">{category}</span>
                    <span className="font-bold">({count})</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium">No notifications</p>
                <p className="text-xs text-gray-400 mt-1">You&apos;re all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {notifications.map((notif) => {
                  const config = CATEGORY_CONFIG[notif.category] || CATEGORY_CONFIG.activity;
                  const IconComponent = getIconComponent(notif.icon, notif.category);
                  
                  return (
                    <div
                      key={notif.notification_id}
                      className={`px-3 sm:px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                        !notif.is_read ? 'bg-violet-50/50' : ''
                      }`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className={`p-1.5 sm:p-2 rounded-full flex-shrink-0 ${config.bg}`}>
                          <IconComponent className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs sm:text-sm font-medium text-gray-900 ${!notif.is_read ? 'font-semibold' : ''}`}>
                            {notif.title}
                          </p>
                          <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {notif.message}
                          </p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded ${config.bg} ${config.color} capitalize`}>
                              {notif.category}
                            </span>
                            <span className="text-[9px] sm:text-[10px] text-gray-400">
                              {formatTime(notif.created_at)}
                            </span>
                          </div>
                        </div>
                        {!notif.is_read && (
                          <div className="w-2 h-2 bg-violet-500 rounded-full flex-shrink-0 mt-2"></div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 sm:px-4 py-2 border-t border-gray-100 bg-gray-50 flex-shrink-0">
            <button
              onClick={handleViewAll}
              className="w-full text-center text-xs sm:text-sm text-violet-600 hover:text-violet-700 font-medium py-1"
            >
              View all & manage preferences →
            </button>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
